// __PLANNING__ (2026-10-26) the Planning workspace's lists, rules, placements and Trello-only cards, through the hub's own
// routes with the signed-in person's token (app.py __PLANNING__; the hub answers this site cross-origin for exactly
// these). GET /api/board/planning, POST /api/board/planning/settings (owner / CEO), POST /api/board/planning/place.
import { supabase } from '../supabaseClient';
import { HUB } from './hubDrafts';

const token = async () => {
  const { data } = await supabase.auth.getSession();
  return (data && data.session && data.session.access_token) || '';
};

async function call(path, opts) {
  const t = await token();
  if (!t) return { ok: false, status: 401, message: 'Not signed in.' };
  try {
    const r = await fetch(HUB + path, { ...(opts || {}), headers: { Authorization: 'Bearer ' + t, ...(opts && opts.body ? { 'Content-Type': 'application/json' } : {}) }, redirect: 'manual' });
    if (r.type === 'opaqueredirect' || r.status === 0) return { ok: false, status: 401, message: 'The hub did not accept this sign-in.' };
    let j = null;
    try { j = await r.json(); } catch (e) { j = null; }
    if (r.status === 200 && j && j.success) return { ok: true, ...j };
    return { ok: false, status: r.status, message: (j && j.error) || ('The hub answered ' + r.status + '.') };
  } catch (e) {
    return { ok: false, status: 0, message: 'The hub could not be reached (' + ((e && e.message) || 'network') + ').' };
  }
}

// { ok, settings, placements, trello: { items, skipped_lists, note }, can_edit_rules, defaults, areas }
export const getPlanning = (fresh) => call('/api/board/planning' + (fresh ? '?fresh=1' : ''));
export const saveSettings = (settings) => call('/api/board/planning/settings', { method: 'POST', body: JSON.stringify({ settings }) });
export const resetSettings = () => call('/api/board/planning/settings', { method: 'POST', body: JSON.stringify({ reset: true }) });
export const placeCard = (key, list, pos) => call('/api/board/planning/place', { method: 'POST', body: JSON.stringify({ key, list, pos }) });
// __PLANNING_ASSIGN__ after a job's driver changed on the board: the hub records it (and a dispatch override with its
// reason), raises the "reconfirm the route" Needs-attention alert for the office, and tells a driver who was on the way.
// -> { ok, alerted, driver_told } | { ok: false, message }
export const reportAssigned = (body) => call('/api/board/planning/assigned', { method: 'POST', body: JSON.stringify(body) });
// __JOB_CHANGES__ (2026-10-26) a job saved here straight to the database (Edit job, Load Details' driver, Route Builder's drag
// or moved pins, Swap Preview): the hub compares it with how it was, writes the dated change line on the job notes and the
// Trello card ("⚠ Changed 26 Oct 14:05 by Henry (office, Edit job): Drop-off A → B") and pushes the driver(s). Best effort:
// a failure never undoes the save. before = the loads row as it was, beforeStops = its load_stops rows (or null).
// __OPEN_DECK__ truck_required: the job's Truck required as it was (billing_data.truck_required; '' = Any truck) - the hub
// compares it, writes the change line and puts the card's "Truck required" line right
const BEFORE_COLS = ['driver_id', 'status', 'scheduled_date', 'commodity', 'weight_kg', 'length_cm', 'width_cm', 'height_cm', 'notes', 'booker_name', 'customer_name', 'truck_required'];
export const reportJobChanged = async (loadId, before, beforeStops, via, extra) => {
  try {
    const b = {};
    BEFORE_COLS.forEach((k) => { if (before && Object.prototype.hasOwnProperty.call(before, k)) b[k] = before[k]; });
    const stops = Array.isArray(beforeStops) ? beforeStops.map((s) => ({ stop_type: s.stop_type, stop_sequence: s.stop_sequence, address: s.address,
      place_name: s.place_name || null, latitude: s.latitude, longitude: s.longitude, time_window_start: s.time_window_start || null,
      time_window_end: s.time_window_end || null, is_completed: !!s.is_completed })) : null;
    return await call('/api/board/planning/job-changed', { method: 'POST', body: JSON.stringify({ load_id: loadId, before: b, before_stops: stops, via, ...(extra || {}) }) });
  } catch (e) {
    return { ok: false, message: (e && e.message) || 'not reported' };
  }
};

// __JOB_ORDER__ (2026-10-27) every driver's job order (top first), kept by the hub and mirrored with his Trello list.
// -> { ok, drivers: { driverId: { ids, at, src, via } }, routes: { routeId: { card_url, driver_id, members } }, flags }
export const getJobOrder = () => call('/api/board/planning/job-order');
// A driver's jobs reordered here (ids: the new order; a partial list reorders just those jobs). routesChanged: a route
// was made / rebuilt, so the hub updates its Trello route card now. -> { ok, ids, changed, trello: 'moving' | 'off' | 'same' }
export const saveJobOrder = (driverId, ids, via, routesChanged) => call('/api/board/planning/job-order', {
  method: 'POST', body: JSON.stringify({ driver_id: driverId, ids: ids || [], via: via || 'hauling board', routes_changed: !!routesChanged }) });
